'use client';

import { useEffect, useState } from 'react';
import {
  getConnectStatus,
  getVendorSettings,
  listShippingProfiles,
  listVendorProducts,
  type VendorSettings,
} from './api';

type Props = {
  vendorUserId: string;
};

type ReadinessItem = {
  id: string;
  label: string;
  done: boolean;
};

export function SellReadinessChecklist({ vendorUserId }: Props) {
  const [items, setItems] = useState<ReadinessItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      getConnectStatus(vendorUserId),
      getVendorSettings(vendorUserId),
      listVendorProducts(vendorUserId),
      listShippingProfiles(vendorUserId).catch(() => ({ profiles: [] })),
    ])
      .then(([connect, settings, products, shipping]) => {
        const activePriced = products.products.filter(
          (p) => p.status === 'active' && p.priceCents > 0,
        );
        const defaultProfile = settings.defaultShippingProfileId
          ? shipping.profiles.find((p) => p.id === settings.defaultShippingProfileId)
          : shipping.profiles.find((p) => p.active);
        setItems([
          {
            id: 'connect',
            label: 'Stripe Connect charges enabled',
            done: connect.connectChargesEnabled && connect.connectOnboardingComplete,
          },
          {
            id: 'store',
            label: 'Store is public with a name',
            done: !!settings.storePublic && !!settings.storeName?.trim(),
          },
          {
            id: 'products',
            label: 'At least one active priced product',
            done: activePriced.length > 0,
          },
          {
            id: 'shipping',
            label: 'Default shipping profile configured',
            done: !!defaultProfile?.active && (settings.allowedShippingCountries?.length ?? 0) > 0,
          },
        ]);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [vendorUserId]);

  if (loading) return <p className="text-muted">Checking sell readiness…</p>;

  const ready = items.length > 0 && items.every((item) => item.done);

  return (
    <section className="sell-readiness-checklist">
      <h3 className="vendor-console__section-title">Sell readiness</h3>
      <p className="vendor-console__hint">
        {ready
          ? 'Your store is ready for hosted checkout.'
          : 'Complete these steps before buyers can check out.'}
      </p>
      <ul className="vendor-onboarding__checklist">
        {items.map((item) => (
          <li key={item.id} className={item.done ? 'done' : ''}>
            {item.label} {item.done ? '✓' : '—'}
          </li>
        ))}
      </ul>
    </section>
  );
}
